import { useMemo } from 'react';
import { useDealerQuery } from '@/features/dealers/hooks/useDealerQuery';
import { useConfigOptionsQuery } from './useConfigOptionsQuery';
import {
    CANCELLATIONS_ALLOWED_KEY,
    deriveFieldMetas,
    toBool,
} from '../services/cancellationRules';
import { CTHF_ENABLED_KEY } from '../services/cthfRules';
import type { CancellationFieldMeta } from '../services/cancellationRules';
import { deriveAdjustmentTypes } from '../utils/deriveAdjustmentTypes';
import type { AdjustmentDerivation } from '../utils/deriveAdjustmentTypes';
import type { CampaignConfigSetting } from '../types/campaign.types';

export interface CancellationFieldMetasResult {
    formFieldMetas: CancellationFieldMeta[];
    derivedFieldMetas: CancellationFieldMeta[];
    masterConfigOptionId?: string;
    adjustment: AdjustmentDerivation;
    isCatalogLoading: boolean;
    isCatalogError: boolean;
}

/** Owns the policies catalogue query, the deduped dealer lookup, the cthf_enabled read and the
 * memoised derivation, so the panel holds none of it directly. */
export const useCancellationFieldMetas = (
    parentDealerId: string | null | undefined,
    settings: CampaignConfigSetting[] | undefined,
    noCustomerRefund: boolean,
): CancellationFieldMetasResult => {
    // per_page=100: the ConfigOptionController default of 10 silently truncates the catalogue.
    const catalogQuery = useConfigOptionsQuery({ group: 'policies', per_page: 100 });
    const catalogOptions = useMemo(() => catalogQuery.data?.data ?? [], [catalogQuery.data]);
    const masterConfigOptionId = catalogOptions.find((o) => o.key_name === CANCELLATIONS_ALLOWED_KEY)?.id;

    const { formFieldMetas, derivedFieldMetas } = useMemo(() => deriveFieldMetas(catalogOptions), [catalogOptions]);

    // ProductDetail already issues this call; React Query dedupes on dealerKeys.detail(id), so a
    // null parentDealerId leaves the query idle (isLoading false, data undefined) with no special case.
    const dealerQuery = useDealerQuery(parentDealerId ?? '');
    // React Query keeps the last good data when a background refetch fails, so a failed lookup
    // must ignore it rather than leave a confident code on screen.
    const dealerType = dealerQuery.isError ? undefined : dealerQuery.data?.type;
    const cthfEnabled = toBool(settings?.find((s) => s.key === CTHF_ENABLED_KEY)?.value);

    const adjustment = useMemo(
        () => deriveAdjustmentTypes(noCustomerRefund, dealerType, cthfEnabled),
        [noCustomerRefund, dealerType, cthfEnabled],
    );

    return {
        formFieldMetas,
        derivedFieldMetas,
        masterConfigOptionId,
        adjustment,
        isCatalogLoading: catalogQuery.isLoading,
        isCatalogError: catalogQuery.isError,
    };
};
